const SOUND_CONFIG = {
  TIMER_DONE: [
    { freq: 880, dur: 0.16, vol: 0.72, delay: 0 },
    { freq: 1320, dur: 0.16, vol: 0.62, delay: 0.18 },
    { freq: 1760, dur: 0.16, vol: 0.52, delay: 0.36 },
  ],
  START_WORK: { freq: 660, dur: 0.14, vol: 0.68, type: 'square' },
  PREP_TICK: {
    3: { freq: 520, dur: 0.08, vol: 0.46, type: 'square' },
    2: { freq: 580, dur: 0.08, vol: 0.46, type: 'square' },
    1: { freq: 640, dur: 0.08, vol: 0.46, type: 'square' },
    default: { freq: 520, dur: 0.08, vol: 0.46, type: 'square' },
  },
  REST_TRANSITION: { freq: 520, dur: 0.12, vol: 0.68, type: 'square' },
};

export default class SoundManager {
  /** Creates a sound manager with default in-memory settings and interaction-gated audio setup. */
  constructor() {
    this._ctx = null;
    this._ctxFailed = false; // Track if AudioContext creation failed
    // App installs persistence hooks after construction, so this initial load uses defaults.
    this.settings = { enabled: true, volume: 1, prepBeeps: true };
    this._activeNodes = new Set();

    // Hooks are defaults at this point; failures still leave usable in-memory settings.
    try {
      const loaded = this._loadSettings();
      if (loaded && typeof loaded === 'object') {
        this.settings = { ...this.settings, ...loaded };
      }
    } catch (e) {
      console.warn('[SoundManager] Failed to load settings:', e);
    }

    // Resume or initialize AudioContext on the first interaction attempt.
    if (typeof document !== 'undefined') {
      const init = () => {
        this._ensureRunning();
        // Remove listeners after this attempt, whether audio initialization succeeds or not.
        document.removeEventListener('pointerdown', init);
        document.removeEventListener('keydown', init);
        document.removeEventListener('touchend', init);
      };
      document.addEventListener('touchend', init, { once: true, passive: true });
      document.addEventListener('pointerdown', init, { once: true, passive: true });
      document.addEventListener('keydown', init, { once: true });
    }
  }

  /** @private Default settings loader; app.js replaces this after construction. @returns {{enabled: boolean, volume: number, prepBeeps: boolean}} */
  _loadSettings() {
    return { enabled: true, volume: 1, prepBeeps: true };
  }
  /** @private Default settings saver; app.js replaces this after construction. @returns {void} */
  _saveSettings() {}

  /** Lazily creates the browser AudioContext, or returns null when unavailable. @returns {AudioContext|null} */
  get ctx() {
    if (this._ctxFailed) return null;
    if (!this._ctx) {
      try {
        const AC =
          typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
        if (AC) {
          this._ctx = new AC();
        }
      } catch (e) {
        console.warn('[SoundManager] Failed to create AudioContext:', e);
        this._ctxFailed = true;
        return null;
      }
    }
    return this._ctx;
  }

  /** Enables or disables sound and stops scheduled nodes when disabling. @param {boolean} v @returns {void} */
  setEnabled(v) {
    this.settings.enabled = !!v;
    if (!v) this.stopAll();
    this._saveSettings();
  }
  /** Persists a clamped volume value; falsy numeric input currently falls back to 1. @param {number|string} v @returns {void} */
  setVolume(v) {
    this.settings.volume = Math.max(0, Math.min(1, +v || 1));
    this._saveSettings();
  }
  /** Enables or disables countdown beeps. @param {boolean} v @returns {void} */
  setPrepBeeps(v) {
    this.settings.prepBeeps = !!v;
    this._saveSettings();
  }

  /** @private Resumes a lazily created context when browser policy permits. @returns {Promise<boolean>} */
  async _ensureRunning() {
    const c = this.ctx;
    if (!c) return false;
    if (c.state === 'suspended') {
      try {
        await c.resume();
      } catch (e) {
        console.warn('[SoundManager] Failed to resume AudioContext:', e);
      }
    }
    return c.state === 'running';
  }

  /** Stops and disconnects every scheduled oscillator. @returns {void} */
  stopAll() {
    this._activeNodes.forEach((node) => {
      try {
        node.stop();
        node.disconnect();
      } catch {
        // Already stopped or disconnected
      }
    });
    this._activeNodes.clear();
  }

  /** Schedules one synthesized beep when sound and AudioContext are available. @param {number} [freq=880] @param {number} [duration=0.15] @param {OscillatorType} [type='sine'] @param {number} [volume=0.22] @param {number} [when=0] @returns {void} */
  beep(freq = 880, duration = 0.15, type = 'sine', volume = 0.22, when = 0) {
    if (!this.settings.enabled) return;
    const ctx = this.ctx;
    if (!ctx) return;

    const adjustedVolume = Math.max(0, Math.min(1, volume * (this.settings.volume ?? 1)));

    // Schedule slightly ahead to avoid browser "too late" errors.
    const t0 = ctx.currentTime + when + 0.005;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);

    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.linearRampToValueAtTime(adjustedVolume, t0 + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);

    this._activeNodes.add(osc);

    osc.onended = () => {
      this._activeNodes.delete(osc);
      osc.disconnect();
      gain.disconnect();
    };

    osc.start(t0);
    osc.stop(t0 + duration + 0.02);
  }

  /** Plays the completion chime sequence. @returns {Promise<void>} */
  async timerDone() {
    const running = await this._ensureRunning();
    if (!running) return;
    this.stopAll();
    SOUND_CONFIG.TIMER_DONE.forEach((s) => {
      this.beep(s.freq, s.dur, s.type || 'sine', s.vol, s.delay);
    });
  }

  /** Plays the work-segment start signal. @returns {Promise<void>} */
  async startWork() {
    const running = await this._ensureRunning();
    if (!running) return;
    this.stopAll();
    const s = SOUND_CONFIG.START_WORK;
    this.beep(s.freq, s.dur, s.type, s.vol);
  }

  /** Plays a preparation countdown tick when enabled. @param {number} nLeft @returns {Promise<void>} */
  async prepTick(nLeft) {
    if (!this.settings.prepBeeps) return;
    const running = await this._ensureRunning();
    if (!running) return;
    const s = SOUND_CONFIG.PREP_TICK[nLeft] || SOUND_CONFIG.PREP_TICK.default;
    this.beep(s.freq, s.dur, s.type, s.vol);
  }

  /** Plays the transition from work to rest. @returns {Promise<void>} */
  async restTransition() {
    const running = await this._ensureRunning();
    if (!running) return;
    this.stopAll();
    const s = SOUND_CONFIG.REST_TRANSITION;
    this.beep(s.freq, s.dur, s.type, s.vol);
  }
}
